import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { fetchFieldMappings, updateSyncMode } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { SyncMode } from "@shared/api-types";
import { SYNC_MODE_LABELS } from "@shared/utils";

interface BulkUpdateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  entityName?: string;
}

const BulkUpdateForm = ({ isOpen, onClose, onSuccess, entityName = "" }: BulkUpdateFormProps) => {
  const [selectedEntity, setSelectedEntity] = useState(entityName);
  const [syncMode, setSyncMode] = useState<SyncMode>(SyncMode.Always);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: fieldMappings, isLoading: isLoadingFields } = useQuery({
    queryKey: ["/api/field-mappings", "field", selectedEntity],
    queryFn: () => fetchFieldMappings(selectedEntity),
    enabled: !!selectedEntity,
  });

  // Reset selected fields when entity changes
  useEffect(() => {
    setSelectedFields([]);
  }, [selectedEntity]);

  const mutation = useMutation({
    mutationFn: updateSyncMode,
    onSuccess: (data) => {
      toast({
        title: "Sync Mode Updated",
        description: `Updated ${data.updatedCount} field mappings to ${SYNC_MODE_LABELS[data.syncMode as SyncMode] || data.syncMode}`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      onSuccess();
    },
    onError: (_error) => {
      toast({
        title: "Error",
        description: "Failed to update sync mode",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!selectedEntity) {
      toast({
        title: "Error",
        description: "Please select an entity",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    if (selectedFields.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one field",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    const request = {
      action: "updateSyncMode" as const,
      entityName: selectedEntity,
      kupFields: selectedFields,
      syncMode,
    };

    mutation.mutate(request);
  };

  const toggleField = (field: string) => {
    setSelectedFields(prev => 
      prev.includes(field) 
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const toggleAllFields = () => {
    if (selectedFields.length === (fieldMappings?.length || 0)) {
      setSelectedFields([]);
    } else {
      setSelectedFields(fieldMappings?.map(mapping => mapping.kupField) || []);
    }
  };

  // Sample entities for demo purposes
  const entities = ["pl_title", "pl_product", "pl_author", "pl_publisher"];

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium">Update Sync Mode</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-1">
                Entity
              </Label>
              <Select value={selectedEntity} onValueChange={setSelectedEntity}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  {entities.map((entity) => (
                    <SelectItem key={entity} value={entity}>
                      {entity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-1">
                Sync Mode
              </Label>
              <Select value={syncMode} onValueChange={(value) => setSyncMode(value as SyncMode)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select sync mode" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.values(SyncMode) as SyncMode[]).map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {SYNC_MODE_LABELS[mode]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-1">
                <Label className="block text-sm font-medium text-gray-700">
                  Apply To
                </Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={toggleAllFields}
                  disabled={!selectedEntity || isLoadingFields}
                >
                  {selectedFields.length === (fieldMappings?.length || 0) ? "Deselect All" : "Select All"}
                </Button>
              </div>
              <Card className="border border-gray-300 rounded-md">
                <CardContent className="max-h-40 overflow-y-auto p-2 pt-6">
                  {isLoadingFields ? (
                    <div className="p-2 text-center text-sm text-gray-500">Loading fields...</div>
                  ) : !selectedEntity ? (
                    <div className="p-2 text-center text-sm text-gray-500">Select an entity to view fields</div>
                  ) : fieldMappings?.length === 0 ? (
                    <div className="p-2 text-center text-sm text-gray-500">No field mappings found for this entity</div>
                  ) : (
                    fieldMappings?.map((mapping) => (
                      <div key={mapping.rowKey} className="p-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id={`field-${mapping.kupField}`}
                            checked={selectedFields.includes(mapping.kupField)}
                            onCheckedChange={() => toggleField(mapping.kupField)}
                          />
                          <Label 
                            htmlFor={`field-${mapping.kupField}`}
                            className="text-sm"
                          >
                            {mapping.kupField} → {mapping.hsField}
                          </Label>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
            
            <Alert variant="destructive" className="bg-yellow-50 p-3 rounded border-yellow-300 !text-yellow-800">
              <AlertTitle className="text-sm font-medium text-yellow-800">Warning</AlertTitle>
              <AlertDescription className="text-sm text-yellow-700">
                This will update the sync mode for all selected field mappings. This may affect data synchronization behavior.
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#0078D4] hover:bg-[#2B88D8]"
              disabled={isLoading || !selectedEntity || selectedFields.length === 0}
            >
              {isLoading ? "Updating..." : "Update Sync Mode"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUpdateForm;
